import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Mail, Phone, MapPin, Clock, Send, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import { toast } from 'sonner';
import { useBotShield } from '../../hooks/useBotShield';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONTENT = {
  hero: { title: 'Contact Us', subtitle: "We'd love to hear from you" },
  info: { email: 'info@readysetstem.org', phone: '331-704-0614', address: '105 E Galena Blvd, STE 200, Aurora, IL 60505' },
  office_hours: { weekdays: '9:00 AM - 6:00 PM', saturday: '10:00 AM - 4:00 PM', sunday: 'Closed' },
  form: { visible: true, title: 'Send us a Message' },
  social: { facebook: '', twitter: '', instagram: '', linkedin: '' }
};

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const content = DEFAULT_CONTENT;
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const { HoneypotField, getPayloadFields } = useBotShield();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, source: 'contact_us', ...getPayloadFields() }),
      });
      if (!res.ok) throw new Error('Server error');
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const socialLinks = [
    { key: 'facebook', icon: Facebook, url: content.social?.facebook },
    { key: 'twitter', icon: Twitter, url: content.social?.twitter },
    { key: 'instagram', icon: Instagram, url: content.social?.instagram },
    { key: 'linkedin', icon: Linkedin, url: content.social?.linkedin }
  ].filter(s => s.url);

  return (
    <div data-testid="contact-page">
      

      {/* Hero */}
      <section className="relative py-24 md:py-32 gradient-hero overflow-hidden">
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">{content.hero?.title || 'Contact Us'}</h1>
            <p className="text-xl text-white/80">{content.hero?.subtitle || ''}</p>
          </div>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="section-padding">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl font-bold mb-8">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Email</p>
                    <a href={`mailto:${content.info?.email}`} className="text-muted-foreground hover:text-primary">{content.info?.email}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Phone</p>
                    <a href={`tel:${content.info?.phone}`} className="text-muted-foreground hover:text-primary">{content.info?.phone}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Address</p>
                    <p className="text-muted-foreground">{content.info?.address}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold mb-1">Office Hours</p>
                    <div className="text-muted-foreground text-sm space-y-1">
                      <p>Monday - Friday: {content.office_hours?.weekdays}</p>
                      <p>Saturday: {content.office_hours?.saturday}</p>
                      <p>Sunday: {content.office_hours?.sunday}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              {socialLinks.length > 0 && (
                <div className="mt-8">
                  <p className="font-semibold mb-4">Follow Us</p>
                  <div className="flex gap-3">
                    {socialLinks.map(({ key, icon: Icon, url }) => (
                      <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center hover:bg-primary hover:text-white transition-colors">
                        <Icon className="w-5 h-5" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Form */}
            {content.form?.visible !== false && (
              <Card className="card-dashboard">
                <CardContent className="p-8">
                  <h2 className="font-heading text-2xl font-bold mb-6">{content.form?.title || 'Send us a Message'}</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <HoneypotField />
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">Name</Label>
                        <Input id="name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input id="email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Phone (optional)</Label>
                        <Input id="phone" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
                      </div>
                      <div>
                        <Label htmlFor="subject">Subject</Label>
                        <Input id="subject" value={formData.subject} onChange={e => setFormData({...formData, subject: e.target.value})} required />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="message">Message</Label>
                      <textarea id="message" value={formData.message} onChange={e => setFormData({...formData, message: e.target.value})} className="w-full min-h-[120px] p-3 border rounded-md resize-none" required />
                    </div>
                    <Button type="submit" className="btn-primary w-full" disabled={submitting} data-testid="contact-submit-btn">
                      {submitting ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
